import type { MessageKey } from '@dijitalapp/i18n';
import {
  MAX_UPLOAD_BYTES,
  MESSAGE_DELETE_WINDOW_HOURS,
  MESSAGE_EDIT_WINDOW_MINUTES,
  MUTE_PRESETS,
  type ContactDto,
  type GroupMemberDto, type ConversationDto, type LinkPreviewDto, type MessageDto } from '@dijitalapp/shared';
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { ACCEPTED_FILES, AttachmentView, tooLarge, VoiceRecorder } from '../components/Attachments';
import { AiAssistDialog, TemplatePicker } from '../components/AssistantUi';
import { CallButtons } from '../components/CallUi';
import { TrustBadge } from '../components/E2eeUi';
import { Icon } from '../components/Icon';
import {
  EncryptedBody,
  firstUrl,
  ForwardDialog,
  LinkifiedText,
  LinkPreviewCard,
  MessageInfoDialog,
  PinnedBar,
  QuickReactions,
  ReactionBar,
  ReplyQuote,
  snippet,
} from '../components/MessageExtras';
import { GroupInfoDialog, NewGroupForm } from '../components/GroupDialogs';
import { ReportDialog, type ReportTarget } from '../components/ReportDialog';
import { api, ApiError, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat, useChatStore } from '../lib/chat-context';
import type { PendingMessage } from '../lib/chat-store';
import { useShell } from './Shell';

const CONNECTION_LABEL: Record<string, MessageKey | null> = {
  connecting: 'conn.connecting',
  reconnecting: 'conn.reconnecting',
  offline: 'conn.offline',
  connected: null,
};

interface SearchHit {
  message: MessageDto;
  conversationId: string;
  title: string;
}

/** Group name, or the other person's name in a direct conversation. */
/** One-line preview of a message for lists: the text, or a label for its attachment. */
function previewText(m: MessageDto, t: ReturnType<typeof useApp>['i18n']['t']): string {
  if (m.contentType === 'encrypted') return t('push.encrypted');
  if (!m.attachment) return m.body ?? '';
  const label = t(
    m.attachment.kind === 'image' ? 'push.photo' : m.attachment.kind === 'voice' ? 'push.voice' : m.attachment.kind === 'video' ? 'push.video' : 'push.file',
  );
  return m.body ? `${label} ${m.body}` : label;
}

export function conversationTitle(c: ConversationDto): string {
  const title = c.group?.title ?? c.peer?.displayName ?? '';
  // Announcement channels already carry 📢 in their generated title.
  return c.type === 'channel' && !title.startsWith('📢') ? `📢 ${title}` : title;
}

/** "N members" for groups, "N subscribers" for channels (only admins see who they are). */
function memberLine(c: ConversationDto, t: ReturnType<typeof useApp>['i18n']['t']): string {
  const count = c.group?.memberCount ?? 0;
  return c.type === 'channel' ? t('channel.subscribers', { count }) : t('group.memberCount', { count });
}

function initials(name: string): string {
  // Strip a leading emoji marker (channels) and take a whole code point, never half a surrogate pair.
  return Array.from(name.replace(/^📢\s*/u, '').trim())[0]?.toUpperCase() ?? '?';
}

function sameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

function useDayLabel() {
  const { i18n } = useApp();
  return (iso: string) => {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today.getTime() - 86_400_000);
    if (sameDay(d, today)) return i18n.t('chat.today');
    if (sameDay(d, yesterday)) return i18n.t('chat.yesterday');
    return i18n.formatDate(d, { dateStyle: 'long' });
  };
}

function shortTime(i18n: ReturnType<typeof useApp>['i18n'], iso: string) {
  const d = new Date(iso);
  return sameDay(d, new Date()) ? i18n.formatDate(d, { timeStyle: 'short' }) : i18n.formatDate(d, { dateStyle: 'short' });
}

/** Chat list shown in the shell's list pane. */
export function ConversationList() {
  const { i18n, me } = useApp();
  const { t } = i18n;
  const chat = useChat();
  const [query, setQuery] = useState('');
  const label = CONNECTION_LABEL[chat.connection];
  const q = query.trim().toLocaleLowerCase(i18n.locale);
  const list = q
    ? chat.conversations.filter((c) => `${conversationTitle(c)} ${c.peer?.username ?? ''}`.toLocaleLowerCase(i18n.locale).includes(q))
    : chat.conversations;

  // Message search runs on the server (bodies are server-side until E2EE); debounced while typing.
  const [messageHits, setMessageHits] = useState<SearchHit[]>([]);
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      const clear = setTimeout(() => setMessageHits([]), 0);
      return () => clearTimeout(clear);
    }
    const timer = setTimeout(() => {
      api<{ messages: SearchHit[] }>('GET', `/search?q=${encodeURIComponent(term)}`).then(
        (r) => setMessageHits(r.messages),
        () => setMessageHits([]),
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="conversation-list">
      <div className="pane-header">
        <label className="visually-hidden" htmlFor="chat-search">
          {t('chat.search')}
        </label>
        <input
          id="chat-search"
          className="input"
          type="search"
          placeholder={t('chat.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <NavLink to="/starred" className="btn" aria-label={t('chat.starred')} title={t('chat.starred')}>
          <span aria-hidden>★</span>
        </NavLink>
        <NavLink to="/messages/new" className="btn" aria-label={t('chat.new')} title={t('chat.new')}>
          <Icon name="plus" />
        </NavLink>
      </div>
      {label && (
        <p className="connection-pill" role="status">
          {t(label)}
        </p>
      )}
      {chat.loaded && list.length === 0 && (!q || messageHits.length === 0) && (
        <p className="muted" style={{ padding: 'var(--space-4)' }}>
          {q ? t('admin.noResults') : t('chat.empty')}
        </p>
      )}
      <ul className="plain-list">
        {list.map((c) => (
          <li key={c.id}>
            <NavLink to={`/messages/${c.id}`} className="list-item conversation-item">
              <span className="avatar" aria-hidden>
                {initials(conversationTitle(c))}
              </span>
              <span className="conversation-text">
                <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
                  <strong className="truncate">{conversationTitle(c)}</strong>
                  <span className="spacer" />
                  {c.lastMessage && <span className="muted nowrap">{shortTime(i18n, c.lastMessage.createdAt)}</span>}
                </span>
                <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
                  <span className="muted truncate">
                    {chat.typing[c.id]
                      ? c.peer
                        ? t('chat.typing', { name: c.peer.displayName })
                        : t('group.someoneTyping')
                      : c.lastMessage
                        ? `${c.lastMessage.senderId === me?.id ? `${t('chat.you')}: ` : ''}${c.lastMessage.deleted ? t('chat.deleted') : previewText(c.lastMessage, t)}`
                        : c.group
                          ? memberLine(c, t)
                          : `@${c.peer?.username ?? ''}`}
                  </span>
                  <span className="spacer" />
                  {c.unreadCount > 0 && (
                    <span className="unread-badge" aria-label={t('chat.unread', { count: c.unreadCount })}>
                      {c.unreadCount}
                    </span>
                  )}
                </span>
              </span>
            </NavLink>
          </li>
        ))}
      </ul>
      {messageHits.length > 0 && (
        <section aria-labelledby="search-messages">
          <h2 id="search-messages" className="muted" style={{ fontSize: 'var(--text-sm)', padding: 'var(--space-3) var(--space-4) 0' }}>
            {t('chat.searchMessages')}
          </h2>
          <ul className="plain-list">
            {messageHits.map((h) => (
              <li key={h.message.id}>
                <NavLink to={`/messages/${h.conversationId}`} className="list-item conversation-item">
                  <span className="conversation-text">
                    <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
                      <strong className="truncate">{h.title}</strong>
                      <span className="spacer" />
                      <span className="muted nowrap">{shortTime(i18n, h.message.createdAt)}</span>
                    </span>
                    <span className="muted truncate">{h.message.body}</span>
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** /messages: nothing selected — contacts and starting a chat. */
export function MessagesHome() {
  const { i18n } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const navigate = useNavigate();
  const { setMobileView } = useShell();
  const [contacts, setContacts] = useState<ContactDto[]>([]);
  const [username, setUsername] = useState('');
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const reload = () => api<{ contacts: ContactDto[] }>('GET', '/contacts').then((r) => setContacts(r.contacts));
  useEffect(() => {
    void api<{ contacts: ContactDto[] }>('GET', '/contacts').then((r) => setContacts(r.contacts));
  }, []);

  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    setStatus(null);
    try {
      await fn();
      if (ok) setStatus({ kind: 'ok', text: ok });
    } catch (err) {
      setStatus({ kind: 'error', text: t(errorMessageKey(err)) });
    }
  };

  const add = (e: FormEvent) => {
    e.preventDefault();
    void act(async () => {
      const c = await api<ContactDto>('POST', '/contacts', { username });
      setUsername('');
      await reload();
      setStatus({ kind: 'ok', text: t('contacts.added', { name: c.displayName }) });
    });
  };

  const message = (c: ContactDto) =>
    act(async () => {
      if (!store) return;
      const conv = await store.startDirect(c.id);
      navigate(`/messages/${conv.id}`);
    });

  return (
    <div className="content-inner">
      <button className="btn mobile-only" type="button" onClick={() => setMobileView('list')}>
        <Icon name="back" label={t('chat.back')} />
      </button>
      <div className="empty-state" style={{ paddingBlock: 'var(--space-5)' }}>
        <Icon name="messages" />
        <h1>{t('chat.select')}</h1>
        <p>{t('chat.selectBody')}</p>
      </div>
      <NewGroupForm />
      <section className="card stack" aria-labelledby="contacts-title">
        <h2 id="contacts-title">{t('contacts.title')}</h2>
        <form className="row" onSubmit={add} style={{ flexWrap: 'nowrap' }}>
          <label className="visually-hidden" htmlFor="contact-username">
            {t('contacts.addLabel')}
          </label>
          <input
            id="contact-username"
            className="input"
            autoCapitalize="none"
            autoComplete="off"
            placeholder={t('contacts.usernamePlaceholder')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <button className="btn btn-primary">{t('contacts.add')}</button>
        </form>
        <p className="hint">{t('contacts.hint')}</p>
        {status && (
          <p role="status" className={status.kind === 'error' ? 'alert alert-error' : 'muted'}>
            {status.text}
          </p>
        )}
        {contacts.length === 0 && <p className="muted">{t('contacts.empty')}</p>}
        {contacts.map((c) => (
          <div key={c.id} className="toggle-row">
            <span className="avatar" aria-hidden>
              {initials(c.displayName)}
            </span>
            <div style={{ minWidth: 0 }}>
              <strong>{c.displayName}</strong> {c.blocked && <span className="badge">{t('contacts.blocked')}</span>}
              <p className="muted">@{c.username}</p>
            </div>
            <span className="spacer" />
            {!c.blocked && (
              <button className="btn btn-primary" type="button" onClick={() => void message(c)}>
                {t('contacts.message')}
              </button>
            )}
            <button
              className="btn btn-danger"
              type="button"
              onClick={() =>
                void act(async () => {
                  if (c.blocked) await api('DELETE', `/blocks/${c.id}`);
                  else await api('POST', '/blocks', { userId: c.id });
                  await reload();
                })
              }
            >
              {c.blocked ? t('contacts.unblock') : t('contacts.block')}
            </button>
          </div>
        ))}
      </section>
    </div>
  );
}

type Row =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'message'; key: string; message: MessageDto; mine: boolean }
  | { kind: 'pending'; key: string; pending: PendingMessage };

function deliveryStatus(m: MessageDto, c: ConversationDto): 'sent' | 'delivered' | 'read' {
  if (m.seq <= c.peerLastReadSeq) return 'read';
  if (m.seq <= c.peerLastDeliveredSeq) return 'delivered';
  return 'sent';
}

const TICKS: Record<string, string> = { sent: '✓', delivered: '✓✓', read: '✓✓', queued: '🕓', sending: '🕓', failed: '!' };

export function ConversationView() {
  const { id = '' } = useParams();
  const { i18n, me, config } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const chat = useChat();
  const { setMobileView } = useShell();
  const dayLabel = useDayLabel();
  const conversation = chat.conversations.find((c) => c.id === id);
  const messages = chat.messages[id];
  const pending = chat.pending[id] ?? [];
  const scroller = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [draft, setDraft] = useState('');
  const [report, setReport] = useState<{ target: ReportTarget; evidence: string[] } | null>(null);
  const [editing, setEditing] = useState<MessageDto | null>(null);
  const [replyTo, setReplyTo] = useState<MessageDto | null>(null);
  const [forwarding, setForwarding] = useState<MessageDto | null>(null);
  const [reactingTo, setReactingTo] = useState<string | null>(null);
  const [infoFor, setInfoFor] = useState<MessageDto | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAi, setShowAi] = useState(false);
  // Link preview for the draft: fetched by the server after a short pause in typing.
  const [linkPreview, setLinkPreview] = useState<{ url: string; preview: LinkPreviewDto | null } | null>(null);
  const [dismissedUrl, setDismissedUrl] = useState<string | null>(null);
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  const [groupMembers, setGroupMembers] = useState<GroupMemberDto[]>([]);
  const isGroup = conversation?.type === 'group' || (conversation?.type === 'channel' && (conversation.group?.myRole === 'owner' || conversation.group?.myRole === 'admin'));
  const memberCount = conversation?.group?.memberCount ?? 0;
  const reloadMembers = () => {
    void api<{ members: GroupMemberDto[] }>('GET', `/groups/${id}/members`).then((r) => setGroupMembers(r.members), () => undefined);
  };
  useEffect(() => {
    if (!isGroup) return;
    void api<{ members: GroupMemberDto[] }>('GET', `/groups/${id}/members`).then((r) => setGroupMembers(r.members), () => undefined);
  }, [id, isGroup, memberCount]);
  const memberName = (userId: string) => groupMembers.find((m) => m.id === userId)?.displayName ?? '…';
  /** Display name for any participant: you, a group member, or the direct-chat peer. */
  const nameOf = (userId: string) =>
    userId === me?.id ? t('chat.you') : isGroup ? memberName(userId) : (conversation?.peer?.displayName ?? '…');
  const byId = new Map((messages ?? []).map((m) => [m.id, m]));
  const canPin = conversation?.type === 'direct' || conversation?.group?.myRole === 'owner' || conversation?.group?.myRole === 'admin';
  const [actionError, setActionError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const canRecord = typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

  const upload = (file: Blob, name: string, opts: { voice?: boolean; durationMs?: number } = {}) => {
    if (!store) return;
    // A typed draft becomes the caption of a photo or file.
    const caption = opts.voice ? '' : draft.trim();
    if (caption) setDraft('');
    stickToBottom.current = true;
    setUploading(true);
    setActionError(null);
    store
      .sendAttachment(id, file, name, { ...opts, caption })
      .catch((err: unknown) => setActionError(t(errorMessageKey(err))))
      .finally(() => setUploading(false));
  };

  const onPickFile = (files: FileList | null) => {
    const file = files?.[0];
    if (fileInput.current) fileInput.current.value = '';
    if (!file) return;
    if (tooLarge(file)) {
      setActionError(t('chat.tooLarge', { mb: Math.round(MAX_UPLOAD_BYTES / 1024 / 1024) }));
      return;
    }
    upload(file, file.name);
  };

  const act = (fn: () => Promise<void>) => {
    setActionError(null);
    void fn().catch((err: unknown) => setActionError(t(errorMessageKey(err))));
  };

  const startEdit = (m: MessageDto) => {
    setEditing(m);
    setDraft(m.body ?? '');
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft('');
  };

  // Ticks every 30 s so edit/delete actions disappear when their window closes (the server enforces it too).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  const canEdit = (m: MessageDto) => !m.deleted && now - new Date(m.createdAt).getTime() < MESSAGE_EDIT_WINDOW_MINUTES * 60_000;
  const canDeleteForEveryone = (m: MessageDto) =>
    !m.deleted && now - new Date(m.createdAt).getTime() < MESSAGE_DELETE_WINDOW_HOURS * 3_600_000;

  const reportMessage = (m: MessageDto) => {
    // Offer up to five earlier messages as optional context; the reported one is always included.
    const list = messages ?? [];
    const index = list.findIndex((x) => x.id === m.id);
    setReport({ target: { type: 'message', messageId: m.id }, evidence: list.slice(Math.max(0, index - 5), index).map((x) => x.id) });
  };

  const mute = async (value: string) => {
    await api('PUT', `/conversations/${id}/mute`, { duration: value === 'off' ? null : value });
    await store?.refreshConversation(id);
  };

  useEffect(() => {
    if (!store) return;
    store.setActive(id);
    stickToBottom.current = true;
    void store.openConversation(id).catch(() => undefined);
    const onVisible = () => store.markRead(id);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      store.setActive(null, id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [store, id]);

  const peerId = conversation?.peer?.id;
  const peerPresence = peerId ? chat.presence[peerId] : undefined;
  useEffect(() => {
    if (store && peerId) void store.loadPresence([peerId]).catch(() => undefined);
  }, [store, peerId]);

  const lastSeq = conversation?.lastSeq ?? 0;
  useEffect(() => {
    store?.markRead(id);
  }, [store, id, lastSeq]);

  const rowCount = (messages?.length ?? 0) + pending.length;
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [rowCount, id]);

  const rows: Row[] = [];
  let lastDay = '';
  for (const m of messages ?? []) {
    const day = new Date(m.createdAt).toDateString();
    if (day !== lastDay) rows.push({ kind: 'day', key: `d-${day}`, label: dayLabel(m.createdAt) });
    lastDay = day;
    rows.push({ kind: 'message', key: m.id, message: m, mine: m.senderId === me?.id });
  }
  for (const p of pending) rows.push({ kind: 'pending', key: p.clientMsgId, pending: p });

  const blockedReason: MessageKey | null = !me?.emailVerified
    ? 'chat.verifyToSend'
    : me.accountState !== 'active'
      ? 'chat.restrictedToSend'
      : conversation && !conversation.canSend
        ? conversation.type === 'channel'
          ? 'channel.subscriberNote'
          : conversation.lastMessage
          ? 'chat.cannotSend'
          : 'chat.waitForReply'
        : null;

  // No server-side link previews in encrypted chats (the server must not learn the links).
  const draftUrl = editing || conversation?.e2ee ? null : firstUrl(draft);
  useEffect(() => {
    if (!draftUrl || draftUrl === dismissedUrl) return;
    let alive = true;
    const timer = setTimeout(() => {
      api<{ preview: LinkPreviewDto | null }>('POST', '/link-previews', { url: draftUrl }).then(
        (r) => alive && setLinkPreview({ url: draftUrl, preview: r.preview }),
        () => undefined,
      );
    }, 600);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [draftUrl, dismissedUrl]);
  const activePreview = draftUrl && draftUrl !== dismissedUrl && linkPreview?.url === draftUrl ? linkPreview.preview : null;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || !store || blockedReason) return;
    if (editing) {
      const target = editing;
      cancelEdit();
      act(() => store.editMessage(id, target.id, body));
      return;
    }
    stickToBottom.current = true;
    setDraft('');
    store.setTyping(id, false);
    const reply = replyTo?.id;
    const previewUrl = activePreview && draftUrl ? draftUrl : undefined;
    setReplyTo(null);
    setDismissedUrl(null);
    void store.send(id, body, reply, previewUrl);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape' && editing) {
      cancelEdit();
    }
  };

  const onDraft = (value: string) => {
    setDraft(value);
    if (!store) return;
    store.setTyping(id, value.length > 0);
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => store.setTyping(id, false), 3000);
  };

  if (!conversation) {
    return chat.loaded ? (
      <div className="empty-state" role="status">
        <p>{t('error.NOT_FOUND')}</p>
      </div>
    ) : (
      <p className="muted">{t('app.loading')}</p>
    );
  }

  return (
    <div className="conversation">
      <header className="conversation-header">
        <button className="btn mobile-only" type="button" onClick={() => setMobileView('list')}>
          <Icon name="back" label={t('chat.back')} />
        </button>
        <span className="avatar" aria-hidden>
          {initials(conversationTitle(conversation))}
        </span>
        <div style={{ minWidth: 0 }}>
          <h1 className="truncate" style={{ fontSize: 'var(--text-md)' }}>
            {conversationTitle(conversation)}
          </h1>
          <p className="muted truncate" aria-live="polite">
            {conversation.group
              ? chat.typing[id]
                ? t('group.someoneTyping')
                : memberLine(conversation, t)
              : chat.typing[id]
                ? t('chat.typing', { name: conversationTitle(conversation) })
                : peerPresence?.online
                  ? t('chat.online')
                  : peerPresence?.lastSeenAt
                    ? t('chat.lastSeen', { date: i18n.formatDate(peerPresence.lastSeenAt) })
                    : `@${conversation.peer?.username ?? ''}`}
          </p>
        </div>
        <span className="spacer" />
        {conversation.e2ee ? (
          conversation.peer && chat.e2eeReady ? (
            <TrustBadge userId={conversation.peer.id} name={conversation.peer.displayName} />
          ) : (
            <span className="badge e2ee-badge" title={`${t('e2ee.label')} — ${t('e2ee.notice')}`}>
              🔒 {t('e2ee.encryptedBadge')}
            </span>
          )
        ) : (
          conversation.type !== 'channel' &&
          (conversation.type === 'direct' || conversation.group?.myRole === 'owner' || conversation.group?.myRole === 'admin') &&
          chat.e2eeReady && (
            <button
              className="btn"
              type="button"
              onClick={() => {
                if (window.confirm(t('e2ee.enableConfirm'))) act(() => store!.enableE2ee(id));
              }}
            >
              🔒 {t('e2ee.enable')}
            </button>
          )
        )}
        <CallButtons conversation={conversation} />
        <label className="visually-hidden" htmlFor="mute">
          {t('mute.label')}
        </label>
        <select
          id="mute"
          className="input header-select"
          value=""
          onChange={(e) => void mute(e.target.value).catch(() => undefined)}
        >
          <option value="" disabled>
            {conversation.mutedUntil ? `🔕 ${t('mute.active')}` : t('mute.label')}
          </option>
          {MUTE_PRESETS.map((p) => (
            <option key={p} value={p}>
              {t(`mute.${p}` as MessageKey)}
            </option>
          ))}
          {conversation.mutedUntil && <option value="off">{t('mute.off')}</option>}
        </select>
        {conversation.group ? (
          <button className="btn" type="button" onClick={() => setGroupInfoOpen(true)}>
            {t('group.info')}
            {conversation.group.pendingRequests > 0 && (
              <span className="badge">{t('group.pendingBadge', { count: conversation.group.pendingRequests })}</span>
            )}
          </button>
        ) : (
          conversation.peer && (
            <button
              className="btn btn-danger"
              type="button"
              onClick={() => {
                const peer = conversation.peer!;
                setReport({
                  target: { type: 'user', userId: peer.id },
                  evidence: (messages ?? []).filter((m) => m.senderId === peer.id).slice(-10).map((m) => m.id),
                });
              }}
            >
              {t('report.user')}
            </button>
          )
        )}
      </header>
      {conversation.pinned.length > 0 && (
        <PinnedBar
          pinned={conversation.pinned}
          canUnpin={canPin}
          name={nameOf}
          onUnpin={(messageId) => act(() => store!.pin(id, messageId, false))}
        />
      )}
      {forwarding && <ForwardDialog message={forwarding} onClose={() => setForwarding(null)} />}
      {infoFor && <MessageInfoDialog message={infoFor} onClose={() => setInfoFor(null)} />}
      {report && <ReportDialog target={report.target} recentMessageIds={report.evidence} onClose={() => setReport(null)} />}
      {groupInfoOpen && conversation.group && (
        <GroupInfoDialog conversation={conversation} members={groupMembers} onChanged={reloadMembers} onClose={() => setGroupInfoOpen(false)} />
      )}

      <div
        className="message-scroller"
        ref={scroller}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
      >
        <p className="encryption-notice">{conversation.e2ee ? `🔒 ${t('e2ee.notice')}` : t('chat.encryptionNotice')}</p>
        {chat.hasMore[id] && (
          <button className="btn" type="button" style={{ alignSelf: 'center' }} onClick={() => void store?.loadOlder(id)}>
            {t('chat.loadOlder')}
          </button>
        )}
        {messages && messages.length === 0 && pending.length === 0 && <p className="muted center">{t('chat.noMessages')}</p>}
        <ol className="message-list" aria-label={conversationTitle(conversation)}>
          {rows.map((r) => {
            if (r.kind === 'day') {
              return (
                <li key={r.key} className="day-separator">
                  <span>{r.label}</span>
                </li>
              );
            }
            const mine = r.kind === 'pending' || r.mine;
            const body = r.kind === 'pending' ? r.pending.body : r.message.body;
            const attachment = r.kind === 'pending' ? r.pending.attachment : r.message.attachment;
            const time = r.kind === 'pending' ? r.pending.createdAt : r.message.createdAt;
            const status = r.kind === 'pending' ? r.pending.status : mine ? deliveryStatus(r.message, conversation) : null;
            return (
              <li key={r.key} id={r.kind === 'message' ? `msg-${r.message.id}` : undefined} className={`bubble-row ${mine ? 'mine' : 'theirs'}`}>
                <div className={`bubble ${status === 'failed' ? 'bubble-failed' : ''}`}>
                  {isGroup && r.kind === 'message' && !r.mine && <span className="bubble-sender">{memberName(r.message.senderId)}</span>}
                  {r.kind === 'message' && r.message.deleted ? (
                    <p className="bubble-text muted">
                      <em>{t('chat.deleted')}</em>
                    </p>
                  ) : (
                    <>
                      {r.kind === 'message' && r.message.forwarded && <span className="forwarded-label">↪ {t('chat.forwarded')}</span>}
                      {r.kind === 'message' && r.message.contentType === 'encrypted' && <EncryptedBody message={r.message} />}
                      {(r.kind === 'message' ? r.message.replyToId : r.pending.replyToId) && (
                        <ReplyQuote target={byId.get((r.kind === 'message' ? r.message.replyToId : r.pending.replyToId)!)} name={nameOf} />
                      )}
                      {attachment && <AttachmentView attachment={attachment} />}
                      {r.kind === 'pending' && r.pending.e2eeFile && <p className="bubble-text">🔒 📎 {r.pending.e2eeFile.name}</p>}
                      {body && (
                        <p className="bubble-text">
                          <LinkifiedText text={body} />
                        </p>
                      )}
                      {r.kind === 'message' && r.message.linkPreview && <LinkPreviewCard preview={r.message.linkPreview} />}
                    </>
                  )}
                  <span className="bubble-meta">
                    {r.kind === 'message' && r.message.editedAt && !r.message.deleted && <span>{t('chat.edited')}</span>}
                    {i18n.formatDate(time, { timeStyle: 'short' })}
                    {status && (
                      <span className={`tick tick-${status}`} title={t(`chat.status.${status}` as MessageKey)}>
                        <span aria-hidden>{TICKS[status]}</span>
                        <span className="visually-hidden">{t(`chat.status.${status}` as MessageKey)}</span>
                      </span>
                    )}
                  </span>
                  {r.kind === 'message' && !r.message.deleted && (
                    <ReactionBar message={r.message} myId={me?.id} name={nameOf} onReact={(emoji) => act(() => store!.react(id, r.message.id, emoji))} />
                  )}
                  {r.kind === 'message' && !r.message.deleted && reactingTo === r.message.id && (
                    <QuickReactions
                      onPick={(emoji) => {
                        setReactingTo(null);
                        act(() => store!.react(id, r.message.id, emoji));
                      }}
                    />
                  )}
                  {r.kind === 'message' && !r.message.deleted && (
                    <span className="msg-actions" role="group" aria-label={t('chat.messageActions')}>
                      {conversation.canSend && (
                        <button
                          className="link-button"
                          type="button"
                          onClick={() => {
                            setEditing(null);
                            setReplyTo(r.message);
                            document.getElementById('composer')?.focus();
                          }}
                        >
                          {t('chat.reply')}
                        </button>
                      )}
                      {!conversation.e2ee && conversation.type !== 'channel' && (
                        <button
                          className="link-button"
                          type="button"
                          aria-expanded={reactingTo === r.message.id}
                          onClick={() => setReactingTo((x) => (x === r.message.id ? null : r.message.id))}
                        >
                          {t('chat.react')}
                        </button>
                      )}
                      {r.message.contentType !== 'encrypted' && (
                        <button className="link-button" type="button" onClick={() => setForwarding(r.message)}>
                          {t('chat.forward')}
                        </button>
                      )}
                      <button
                        className="link-button"
                        type="button"
                        aria-pressed={chat.starred.has(r.message.id)}
                        onClick={() => act(() => store!.star(id, r.message.id, !chat.starred.has(r.message.id)))}
                      >
                        {chat.starred.has(r.message.id) ? `★ ${t('chat.unstar')}` : t('chat.star')}
                      </button>
                      {r.mine && conversation.type !== 'channel' && (
                        <button className="link-button" type="button" onClick={() => setInfoFor(r.message)}>
                          {t('chat.info')}
                        </button>
                      )}
                      {canPin && (
                        <button
                          className="link-button"
                          type="button"
                          onClick={() => {
                            const pinned = conversation.pinned.some((p) => p.messageId === r.message.id);
                            act(() => store!.pin(id, r.message.id, !pinned));
                          }}
                        >
                          {conversation.pinned.some((p) => p.messageId === r.message.id) ? t('chat.unpin') : t('chat.pin')}
                        </button>
                      )}
                      {r.mine && canEdit(r.message) && r.message.contentType !== 'encrypted' && (
                        <button className="link-button" type="button" onClick={() => startEdit(r.message)}>
                          {t('chat.edit')}
                        </button>
                      )}
                      {r.mine && canDeleteForEveryone(r.message) && (
                        <button
                          className="link-button"
                          type="button"
                          onClick={() => act(() => store!.deleteMessage(id, r.message.id, 'everyone'))}
                        >
                          {t('chat.deleteForEveryone')}
                        </button>
                      )}
                      <button className="link-button" type="button" onClick={() => act(() => store!.deleteMessage(id, r.message.id, 'me'))}>
                        {t('chat.deleteForMe')}
                      </button>
                      {!r.mine && (
                        <button className="link-button" type="button" onClick={() => reportMessage(r.message)}>
                          {t('report.message')}
                        </button>
                      )}
                    </span>
                  )}
                  {r.kind === 'pending' && r.pending.status === 'failed' && (
                    <span className="row" style={{ gap: 'var(--space-2)' }}>
                      <span className="hint">{t(errorMessageKey(new ApiError(r.pending.error ?? 'UNKNOWN', 0)))}</span>
                      <button className="btn" type="button" onClick={() => void store?.retry(r.pending)}>
                        {t('chat.retry')}
                      </button>
                      <button className="btn btn-danger" type="button" onClick={() => store?.discard(r.pending)}>
                        {t('chat.discard')}
                      </button>
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {blockedReason ? (
        <p className="composer-blocked" role="status">
          {t(blockedReason)}
        </p>
      ) : (
        <form className="composer" onSubmit={submit}>
          {recording && (
            <VoiceRecorder
              onDone={(clip, durationMs, ext) => {
                setRecording(false);
                upload(clip, `voice.${ext}`, { voice: true, durationMs });
              }}
              onCancel={() => setRecording(false)}
              onError={(message) => {
                setRecording(false);
                setActionError(message);
              }}
            />
          )}
          {uploading && (
            <div className="composer-banner" role="status">
              {t('chat.uploading')}
            </div>
          )}
          {activePreview && (
            <div className="composer-banner composer-preview">
              <LinkPreviewCard preview={activePreview} onRemove={() => setDismissedUrl(draftUrl)} />
            </div>
          )}
          {replyTo && !editing && (
            <div className="composer-banner" role="status">
              <span className="truncate">
                {t('chat.replyingTo', { name: nameOf(replyTo.senderId) })}: {snippet(t, replyTo) || t('chat.attachmentLabel')}
              </span>
              <span className="spacer" />
              <button className="link-button" type="button" onClick={() => setReplyTo(null)}>
                {t('common.cancel')}
              </button>
            </div>
          )}
          {(editing || actionError) && (
            <div className="composer-banner" role="status">
              {actionError ?? t('chat.editing')}
              <span className="spacer" />
              <button className="link-button" type="button" onClick={editing ? cancelEdit : () => setActionError(null)}>
                {t('common.cancel')}
              </button>
            </div>
          )}
          {!editing && (!conversation.e2ee || chat.e2eeReady) && (
            <>
              <input
                ref={fileInput}
                type="file"
                className="visually-hidden"
                accept={ACCEPTED_FILES}
                tabIndex={-1}
                aria-hidden
                onChange={(e) => onPickFile(e.target.files)}
              />
              <button className="btn" type="button" disabled={uploading || recording} onClick={() => fileInput.current?.click()}>
                <Icon name="attach" label={t('chat.attach')} />
              </button>
            </>
          )}
          {!editing && (
            <>
              <button className="btn" type="button" onClick={() => setShowTemplates(true)} aria-label={t('tpl.open')} title={t('tpl.open')}>
                📋
              </button>
              <button className="btn" type="button" disabled={!draft.trim()} onClick={() => setShowAi(true)} aria-label={t('ai.open')} title={t('ai.open')}>
                ✨
              </button>
            </>
          )}
          {showTemplates && (
            <TemplatePicker draft={draft} onInsert={(text) => onDraft(draft.trim() ? `${draft.trimEnd()} ${text}` : text)} onClose={() => setShowTemplates(false)} />
          )}
          {showAi && <AiAssistDialog draft={draft} encrypted={conversation.e2ee} onApply={(text) => onDraft(text)} onClose={() => setShowAi(false)} />}
          <label className="visually-hidden" htmlFor="composer">
            {t('chat.placeholder')}
          </label>
          <textarea
            id="composer"
            className="input composer-input"
            rows={1}
            placeholder={t('chat.placeholder')}
            value={draft}
            maxLength={config?.remoteConfig.maxMessageLength}
            onChange={(e) => onDraft(e.target.value)}
            onKeyDown={onKeyDown}
          />
          {!draft.trim() && !editing && canRecord && (!conversation.e2ee || chat.e2eeReady) ? (
            <button className="btn btn-primary" type="button" disabled={uploading || recording} onClick={() => setRecording(true)}>
              <Icon name="mic" label={t('chat.record')} />
            </button>
          ) : (
            <button className="btn btn-primary" disabled={!draft.trim()}>
              {editing ? t('chat.save') : t('chat.send')}
            </button>
          )}
        </form>
      )}
    </div>
  );
}
